import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Eye, MousePointerClick, TrendingUp, ExternalLink } from "lucide-react";
import { api, formatDate } from "@/lib/api";

/** Per-post view + CTA-click analytics table. Populates from
 *  `GET /admin/blog/analytics`. Read-only — sorting done server-side by views desc. */
export default function BlogAnalyticsPanel() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["adminBlogAnalytics"],
    queryFn: async () => (await api.get("/admin/blog/analytics")).data,
    refetchInterval: 60000,
  });

  if (isLoading) {
    return <div className="rounded-2xl border border-gg-border bg-gg-surface p-8 text-center text-gg-muted">Loading analytics…</div>;
  }
  const posts = data?.posts || [];
  const totals = data?.totals || { views: 0, unique_views: 0, cta_clicks: 0 };

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid sm:grid-cols-3 gap-3">
        <StatCard icon={<Eye size={18} />} label="Total views" value={totals.views.toLocaleString()} testId="analytics-total-views" />
        <StatCard icon={<TrendingUp size={18} />} label="Unique visitors" value={totals.unique_views.toLocaleString()} accent="brown" />
        <StatCard icon={<MousePointerClick size={18} />} label="CTA clicks" value={totals.cta_clicks.toLocaleString()} accent="green" testId="analytics-cta-clicks" />
      </div>

      {posts.length === 0 ? (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-8 text-center text-gg-muted">
          No published posts yet — analytics will populate as visitors read your journal.
        </div>
      ) : (
        <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Post</th>
                  <th className="text-right px-4 py-3 font-semibold">Views</th>
                  <th className="text-right px-4 py-3 font-semibold">Unique</th>
                  <th className="text-right px-4 py-3 font-semibold">CTA clicks</th>
                  <th className="text-right px-4 py-3 font-semibold">CTR</th>
                  <th className="px-4 py-3 font-semibold">CTA type</th>
                  <th className="px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.id} className="border-t border-gg-border" data-testid={`analytics-row-${p.slug}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--gg-text)] line-clamp-1">{p.title}</div>
                      <div className="text-xs text-gg-muted mt-0.5">
                        {p.published ? formatDate(p.published_at) : <span className="text-[#9b6a26]">Draft</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{p.views_total}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.views_unique}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.cta_clicks}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      <span className={p.ctr >= 5 ? "text-gg-green font-semibold" : p.ctr === 0 ? "text-gg-muted" : ""}>
                        {p.ctr}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {p.cta_type === "none" ? (
                        <span className="text-xs text-gg-muted italic">no CTA</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gg-brown/10 text-gg-brown">
                          {p.cta_type}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-3">
                      <Link
                        to={`/blog/${p.slug}`}
                        target="_blank"
                        rel="noopener"
                        className="text-gg-muted hover:text-gg-green"
                        title="Open post"
                      >
                        <ExternalLink size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-right">
        <button
          type="button"
          onClick={() => refetch()}
          className="btn-pill btn-outline text-xs"
        >
          Refresh
        </button>
      </div>
      <p className="text-xs text-gg-muted italic">
        Views auto-refresh every minute. &quot;Unique&quot; counts distinct visitor fingerprints (IP + browser, no cookies stored). &quot;CTR&quot; = clicks ÷ total views. Aim for &gt;5% on posts with a CTA.
      </p>
    </div>
  );
}

function StatCard({ icon, label, value, accent, testId }) {
  const color =
    accent === "green" ? "text-gg-green" :
    accent === "brown" ? "text-gg-brown" :
    "text-[var(--gg-text)]";
  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
        {icon} {label}
      </div>
      <p className={`font-heading text-4xl mt-2 leading-none ${color}`}>{value}</p>
    </div>
  );
}
